
import { Component, Vue } from 'vue-property-decorator';
import { State } from 'vuex-class';
import GenericSearchBar from './GenericSearchBar.vue';
import { ITag, TagType } from './TagModel';
import {FilterModel, FilterItem, ConsultantType, QuickFilter} from '../filter/FilterModel';
import { EmployeeDataType } from '@/data-management/data-sources/sources/employees-data-source';
import { eventService } from "@/shared/services/EventService";
import AllFiltersButton from '../filter/AllFiltersButton.vue';
import { IActiveAccount } from '@/contracts/AccountContract';
import { ISkill } from '@/contracts/SkillContract';
import { ITribe } from '@/contracts/TribeContract';
import { IDataState, IFilterState } from '@/store/contract';
import { IBeehiveState } from '@/store/store';
import { IHive } from "@/contracts/HiveContract";
import { ICapability } from "@/contracts/CapabilityContract";
import { ISeniority, CoachRole } from "@/contracts/EmployeeContract";
import { seniorityMap } from "@/shared/SeniorityMap";
import { IAccountCluster } from "@/contracts/AccountClusterContract";
import {TagOption, TagOptions} from '@/data-management/tag-options';
import * as moment from 'moment';
import { Moment } from 'moment';

@Component({
    components: {
        GenericSearchBar,
        AllFiltersButton
    }
})
export default class TopSearchBar extends Vue {
    @State('data') dataState: IDataState;
    @State('filter') filterState: IFilterState;

    private tagData: ITag[] = [];

    public created() {
        this.initSuggestionData();
    }

    public onTagsChanged(newTags: ITag[]) {
        let personTag = newTags.find(tag => tag.tagType === TagType.Person);
        if (personTag) {
            this.selectPerson(personTag.value);
            return;
        }

        let skills = newTags.filter(tag => tag.tagType === TagType.Skill).map(tag => tag.value);
        let tribes = newTags.filter(tag => tag.tagType === TagType.Tribe).map(tag => tag.value);
        let hives = newTags.filter(tag => tag.tagType === TagType.Hive).map(tag => tag.value);
        let profiles = newTags.filter(tag => tag.tagType === TagType.Profile).map(tag => tag.value);
        let seniorities = newTags.filter(tag => tag.tagType === TagType.Seniority).map(tag => tag.value);
        let accounts = newTags.filter(tag => tag.tagType === TagType.Account).map(tag => tag.value);
        let accountClusters = newTags.filter(tag => tag.tagType === TagType.AccountCluster).map(tag => tag.value);
        let consultantTypes = newTags.filter(tag => tag.tagType === TagType.ConsultantType).map(tag => tag.value);
        let peopleCoaches = newTags.filter(tag => tag.tagType === TagType.PeopleCoach).map(tag => tag.value);
        let quickFilters = newTags.filter(tag => tag.tagType === TagType.QuickFilter).map(tag => tag.value);
        let startDateFrom = this.extractMomentFromDateTag(newTags, TagType.StartDateFrom);
        let startDateTo = this.extractMomentFromDateTag(newTags, TagType.StartDateTo);

        this.filter(skills, tribes, hives, profiles, seniorities, accounts, accountClusters, consultantTypes, peopleCoaches, quickFilters, startDateFrom, startDateTo);
    }

    public onTagAdded(tag: ITag) {
        eventService.track('Search', tag.tagType, tag.text);
    }

    private initSuggestionData() {
        this.tagData.push(...((this.$store.state as IBeehiveState).dataSourceLoader.employeesDataSource.data.slice() as EmployeeDataType[]).map(e => this.mapToTag(e, TagType.Person)));
        this.tagData.push(...this.dataState.activeAccounts.map(a => this.mapToTag(a, TagType.Account)));
        this.tagData.push(...this.dataState.accountClusters.map(a => this.mapToTag(a, TagType.AccountCluster)));
        this.tagData.push(...this.dataState.skills.map(s => this.mapToTag(s, TagType.Skill)));
        this.tagData.push(...this.dataState.tribes.map(s => this.mapToTag(s, TagType.Tribe)));
        this.tagData.push(...this.dataState.hives.map(s => this.mapToTag(s, TagType.Hive)));
        this.tagData.push(...this.dataState.capabilities.map(s => this.mapCapabilityToProfileTag(s)));
        this.tagData.push(...((this.$store.state as IBeehiveState).dataSourceLoader.employeesDataSource.functionSeniorShipCodes.slice() as string[]).map(e => {
            const seniority = seniorityMap[e] || { Name: e, Code: e, Order: -1 };
            return this.mapToTag(seniority, TagType.Seniority);
        }));
    }

    get getSelectedTags(): ITag[] {
      if (!this.filterState.filterModel) {
        return [];
      }

      let tagData: ITag[] = [];

      // Skills
      tagData.push(...this.filterState.filterModel.skills
          .flatMap(s => {
            const skill = this.dataState.skills.find(sk => sk.Id === s.value);
            return skill ? [this.mapToTag(skill, TagType.Skill)] : [];
          }));

      // Tribes
      tagData.push(...this.filterState.filterModel.tribes
          .flatMap(t => {
            const tribe = this.dataState.tribes.find(tr => tr.TribeId === t.value);
            return tribe ? [this.mapToTag(tribe, TagType.Tribe)] : [];
          }));

      // Hives
      tagData.push(...this.filterState.filterModel.hives
          .flatMap(h => {
            const hive = this.dataState.hives.find(hv => hv.HiveId === h.value);
            return hive ? [this.mapToTag(hive, TagType.Hive)] : [];
          }));

      // Profiles
      tagData.push(...this.filterState.filterModel.profiles
          .flatMap(p => {
            const capability = this.dataState.capabilities.find(cap => cap.code === p.value);
            return capability ? [this.mapCapabilityToProfileTag(capability)] : [];
          }));

      // Seniorities
      tagData.push(...this.filterState.filterModel.seniority
          .flatMap(s => {
            const seniority = seniorityMap[s.value] || { Name: s.value, Code: s.value, Order: -1 };
            return [this.mapToTag(seniority, TagType.Seniority)];
          }));

      // Accounts
      tagData.push(...this.filterState.filterModel.accounts
          .flatMap(a => {
            const account = this.dataState.activeAccounts.find(acc => acc.Id === a.value);
            return account ? [this.mapToTag(account, TagType.Account)] : [];
          }));

      // Account Clusters
      tagData.push(...this.filterState.filterModel.accountClusters
          .flatMap(ac => {
            const cluster = this.dataState.accountClusters.find(c => c.ClusterId === ac.value);
            return cluster ? [this.mapToTag(cluster, TagType.AccountCluster)] : [];
          }));

      // Consultant Types
      tagData.push(...this.filterState.filterModel.consultantTypes
          .flatMap(ct => {
            const option = TagOptions.getConsultantTypeOptions().find(c => c.value === ct.value);
            return option ? [this.mapTagOptionToTag(option, TagType.ConsultantType)] : [];
          }));

      // People Coaches
      tagData.push(...this.filterState.filterModel.peopleCoaches
          .flatMap(pc => {
            const option = TagOptions.getPeopleCoachesOptions().find(c => c.value === pc.value);
            return option ? [this.mapTagOptionToTag(option, TagType.PeopleCoach)] : [];
          }));

      // Quick Filters
      tagData.push(...this.filterState.filterModel.quickFilters
          .flatMap(qf => {
            const option = TagOptions.getQuickFiltersOptions().find(f => f.value === qf.value);
            return option ? [this.mapTagOptionToTag(option, TagType.QuickFilter)] : [];
          }));

      // Date Filters - Start Date From
      if (this.filterState.filterModel.startDateFrom) {
        tagData.push( {
          text: `From: ${this.filterState.filterModel.startDateFrom.format('DD/MM/YYYY')}`,
          tagType: TagType.StartDateFrom,
          value: this.filterState.filterModel.startDateFrom as Moment
        });
      }

      // Date Filters - Start Date To
      if (this.filterState.filterModel.startDateTo) {
        tagData.push( {
          text: `To: ${this.filterState.filterModel.startDateTo.format('DD/MM/YYYY')}`,
          tagType: TagType.StartDateTo,
          value: this.filterState.filterModel.startDateTo as Moment
        });
      }

      return tagData;
    }


    private clear() {
        this.filterState.filterModel = new FilterModel();
        this.filterState.filtersAreApplied = false;
        (this.$store.state as IBeehiveState).commandManager.filterCommandExecutor.requestExecute({
            filter: new FilterModel()
        });
    }

    private selectPerson(person: EmployeeDataType) {
        this.clear();
        (this.$store.state as IBeehiveState).commandManager.selectUserCommandExecutor.requestExecute({
            userId: person._internal_datasource_id_,
            forceSelect: true
        });
    }

    private selectAccount(account: IActiveAccount) {
        this.clear();
        (this.$store.state as IBeehiveState).commandManager.selectAccountCommandExecutor.requestExecute({
            accountId: account.Id,
            enable: true
        });
    }


    private filter(skills: ISkill[], tribes: ITribe[], hives: IHive[], profiles: ICapability[],  seniorities: ISeniority[], accounts: IActiveAccount[], accountClusters: IAccountCluster[], consultantTypes: {label: string, value: ConsultantType}[], peopleCoaches: {label: string, value: CoachRole}[], quickFilters: {label: string, value: QuickFilter}[], startDateFrom: Moment | null, startDateTo: Moment | null) {
      let filterModel = new FilterModel();

      // If there's an existing filter, copy its current state
      if (this.filterState.filterModel) {
        this.filterState.filterModel.copyTo(filterModel);
      }

      // Only update categories with selected tags
      filterModel.updateSkills(skills.length ? skills.map(skill => new FilterItem(skill.Name, skill.Id)) : []);
      filterModel.updateTribes(tribes.length ? tribes.map(tribe => new FilterItem(tribe.Name, tribe.TribeId)) : []);
      filterModel.updateHives(hives.length ? hives.map(hive => new FilterItem(hive.Name, hive.HiveId)) : []);
      filterModel.updateProfiles(profiles.length ? profiles.map(profile => new FilterItem(profile.label, profile.code)) : []);
      filterModel.updateSeniority(seniorities.length ? seniorities.map(seniority => new FilterItem(seniority.Name, seniority.Code || seniority.Name)) : []);
      filterModel.updateAccounts(accounts.length ? accounts.map(account => new FilterItem(account.Name, account.Id)) : []);
      filterModel.updateAccountClusters(accountClusters.length ? accountClusters.map(cluster => new FilterItem(cluster.Name, cluster.ClusterId)) : []);
      filterModel.updateConsultantType(consultantTypes.length ? consultantTypes.map(consultantType => new FilterItem(consultantType.label, consultantType.value)) : []);
      filterModel.updatePeopleCoaches(peopleCoaches.length ? peopleCoaches.map(coach => new FilterItem(coach.label, coach.value)) : []);
      filterModel.updateQuickFilters(quickFilters.length ? quickFilters.map(filter => new FilterItem(filter.label, filter.value)) : []);
      filterModel.updateStartDateFrom(startDateFrom);
      filterModel.updateStartDateTo(startDateTo);

      // Update the filter state
      this.filterState.filterModel = filterModel;
      this.filterState.filtersAreApplied = !filterModel.isEmpty();
      (this.$store.state as IBeehiveState).commandManager.filterCommandExecutor.requestExecute({
        filter: filterModel
      });
    }

    private mapToTag(item: { Name: string }, tagType: TagType): ITag {
         return {
            text: item.Name,
            tagType,
            value: item
        };
    }

    private mapTagOptionToTag(option: TagOption, tagType: TagType): ITag {
        return {
            text: option.label,
            tagType,
            value: option
        };
    }

    private mapCapabilityToProfileTag(capability: ICapability): ITag {
        return {
            text: capability.label,
            tagType: TagType.Profile,
            value: capability
        };
    }

    private extractMomentFromDateTag(tags: ITag[], tagType: TagType): Moment | null {
        const tag = tags.find(t => t.tagType === tagType);
        if (!tag?.value) {
          return null;
        }

        // If it's already a Moment object, return it
        if (moment.isMoment(tag.value)) {
          return tag.value;
        }

        // If it's a string or other serialized format, parse it
        const parsedMoment = moment(tag.value);
        return parsedMoment.isValid() ? parsedMoment : null;
    }
}
